import { useEffect, useRef } from "react";
import { api } from "@/lib/api-client";

/**
 * Lane Q-b pt2 (F-054) — INSTANT release of the operator's doc/batch
 * assignment the moment they LEAVE the Verification Station.
 *
 * Background: when an operator abandons the VS (route-away, tab-close, idle),
 * today's release path is slow — the 15s heartbeat stops on unmount, the Redis
 * editing-lock TTL-expires (~60s), and only then does the 60s server sweep
 * (Q-b pt1) revert the DB `assigned` status. Net effect: the doc sits in limbo
 * for 1–2 minutes, invisible to other operators. This hook collapses that to
 * an immediate release on exit.
 *
 * Two exit paths are covered:
 *   1. React unmount / route-away (operator clicks away inside the SPA) →
 *      `api.delete('/documents/:id/lock')`, the SAME release route the manual
 *      "Check In Batch" flow ultimately exercises (DELETE /documents/:id/lock
 *      drops the Redis lock AND reverts assignedTo/status via
 *      queueManager.releaseDocument).
 *   2. Hard tab close / browser navigation (`beforeunload` / `pagehide`) →
 *      `navigator.sendBeacon` to a tiny POST sibling endpoint. sendBeacon is
 *      synchronous-dispatch, survives page teardown, and sends the session
 *      cookie automatically — but it can ONLY POST and can't set DELETE/headers,
 *      so the regular DELETE route is unusable here; the beacon endpoint is the
 *      POST wrapper around the identical release logic.
 *
 * Critical: this must NOT fire on advance-to-next-document WITHIN the station.
 * Advance calls `navigate('/verify/<nextId>')`, which only changes the route
 * PARAM — React Router keeps VerificationStation mounted and swaps documentId,
 * so the unmount cleanup naturally does NOT run on advance. As belt-and-braces
 * (and to stay correct if the route is ever keyed/remounted), the caller can
 * raise `suppressRef.current = true` immediately before an intentional advance;
 * the unmount cleanup honours it and skips the release.
 */

/** Path (relative to the /api base) of the beacon-friendly POST release. */
export function RELEASE_BEACON_PATH(documentId: string): string {
  return `/documents/${documentId}/release-beacon`;
}

interface UseReleaseOnExitArgs {
  /** The document currently loaded in the station (route param). */
  documentId: string | undefined;
  /** Whether the station owns this doc's lock (false in terminal mode). */
  active: boolean;
  /**
   * Set `.current = true` synchronously before an INTENTIONAL exit that should
   * NOT trigger a release (e.g. the VS already released as part of accept, or a
   * future keyed-route advance remount). Read fresh at unmount via the ref, so
   * a value set after the last render still reaches the cleanup closure.
   */
  suppressRef?: React.RefObject<boolean | null>;
}

export function useReleaseOnExit({
  documentId,
  active,
  suppressRef,
}: UseReleaseOnExitArgs): void {
  // Keep the latest documentId / active accessible to the unmount cleanup and
  // the beforeunload handler without re-binding them on every doc change. The
  // unmount effect uses [] deps (fires only on true unmount); reading from refs
  // means it always releases whatever doc was current at exit time.
  const documentIdRef = useRef(documentId);
  documentIdRef.current = documentId;
  const activeRef = useRef(active);
  activeRef.current = active;
  // Ref-of-ref so the cleanup closure always sees the caller's latest ref.
  const suppressRefRef = useRef(suppressRef);
  suppressRefRef.current = suppressRef;

  // Path 1 — React unmount / route-away. Empty deps → the cleanup runs ONLY
  // when the station truly unmounts (operator left /verify), NOT on each
  // documentId change (advance-to-next keeps the station mounted).
  useEffect(() => {
    return () => {
      if (suppressRefRef.current?.current) return;
      if (!activeRef.current) return;
      const docId = documentIdRef.current;
      if (!docId) return;
      // Fire-and-forget; the operator is already leaving. A failure here just
      // falls back to the existing TTL-expiry + server sweep (Q-b pt1).
      void Promise.resolve(api.delete(`/documents/${docId}/lock`)).catch(
        () => void 0,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Path 2 — hard tab close / browser navigation. beforeunload + pagehide
  // (pagehide covers mobile Safari / bfcache where beforeunload is unreliable).
  // sendBeacon is sync-dispatch and survives teardown; cookie auth rides along
  // automatically (credentials are sent for same-origin beacons).
  useEffect(() => {
    const handler = () => {
      if (suppressRefRef.current?.current) return;
      if (!activeRef.current) return;
      const docId = documentIdRef.current;
      if (!docId) return;
      if (typeof navigator.sendBeacon !== "function") return;
      // Empty JSON body — the endpoint takes no payload; the doc id is in the
      // path. A Blob with application/json type keeps Fastify's CTP parser happy.
      const body = new Blob(["{}"], { type: "application/json" });
      navigator.sendBeacon(`/api${RELEASE_BEACON_PATH(docId)}`, body);
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
